/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { formatLocalizedNumber } from './number-format';

export type MaskFunction = (value: string) => {
  unmasked: string;
  masked: string;
};

/**
 * Normalizes a raw money/area input string to a fixed two-decimal number string.
 *
 * When the input already carries a decimal separator the digits are treated as
 * cents (divided by 100); otherwise the whole-number digits are used directly.
 *
 * @param {string} value - The raw input string (may contain separators/symbols).
 * @returns {string} A `toFixed(2)` numeric string (e.g. `"1234.56"`).
 */
const toFixedDecimalString = (value: string): string => {
  const digits = Number(value.replace(/\D+/g, ""));
  const hasDecimal = value.includes('.') || value.includes(',');
  return (hasDecimal ? digits / 100 : digits).toFixed(2);
};

export const numberMask: MaskFunction = (value) => {
  const unmasked = value.replace(/\D+/g, "");
  const masked = unmasked;
  return { unmasked, masked };
};

export const ccvMask: MaskFunction = (value) => {
  const clamped = value.replaceAll(/[^\d]/g, "").slice(0, 3);
  return { unmasked: clamped, masked: clamped };
};

export const cpfMask: MaskFunction = (value) => {
  const unmasked = value.replace(/\D+/g, "").slice(0, 11);
  const masked = unmasked
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})/, "$1-$2")
    .replace(/(-\d{2})\d+$/, "$1");
  return { unmasked, masked };
};

/**
 * Formats a CNPJ as `XX.XXX.XXX/XXXX-DD`.
 *
 * Letters survive in the first 12 positions and are upper-cased: since
 * Receita Federal's July 2026 rollout the raiz and ordem are alphanumeric
 * (`12.ABC.345/01DE-35` is a real shape). The two check digits stay
 * numeric, so anything non-numeric typed there is dropped.
 *
 * Built by slicing rather than by chained regexes because the separators
 * are no longer distinguishable from content -- a `\d`-based chain would
 * skip the letters and misplace every dot.
 */
export const cnpjMask: MaskFunction = (value) => {
  const cleaned = value.replace(/[^0-9A-Za-z]+/g, "").toUpperCase();
  const unmasked = cleaned.slice(0, 12) + cleaned.slice(12, 14).replace(/[^0-9]+/g, "");
  const groups: [string, string][] = [
    [".", unmasked.slice(2, 5)],
    [".", unmasked.slice(5, 8)],
    ["/", unmasked.slice(8, 12)],
    ["-", unmasked.slice(12, 14)],
  ];
  const masked = groups.reduce(
    (acc, [separator, group]) => (group ? acc + separator + group : acc),
    unmasked.slice(0, 2),
  );
  return { unmasked, masked };
};

export const phoneMask: MaskFunction = (value) => {
  const unmasked = value.replace(/\D+/g, "");
  const masked = unmasked
    .replace(/(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{4})(\d)/, "$1-$2")
    .replace(/(\d{4})-(\d)(\d{4})/, "$1$2-$3")
    .replace(/(-\d{4})\d+$/, "$1");
  return { unmasked, masked };
};

export const phoneDDIMask: MaskFunction = (value) => {
  const unmasked = value.replace(/\D+/g, "");
  const masked = unmasked
    .replace(/(\d{2})(\d)/, "+$1 $2")
    .replace(/(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{4})(\d)/, "$1-$2")
    .replace(/(\d{4})-(\d)(\d{4})/, "$1$2-$3")
    .replace(/(-\d{4})\d+$/, "$1");
  return { unmasked, masked };
};

export const cepMask: MaskFunction = (value) => {
  const unmasked = value.replace(/\D+/g, "").slice(0, 8);
  let masked = unmasked;
  if (unmasked.length > 5) {
    masked = `${unmasked.slice(0, 2)}.${unmasked.slice(2, 5)}-${unmasked.slice(5)}`;
  } else if (unmasked.length > 2) {
    masked = `${unmasked.slice(0, 2)}.${unmasked.slice(2)}`;
  }
  return { unmasked, masked };
};

export const pisMask: MaskFunction = (value) => {
  const unmasked = value.replace(/\D+/g, "");
  const masked = unmasked
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{5})(\d)/, "$1.$2")
    .replace(/(\d{5}\.)(\d{2})(\d)/, "$1$2-$3")
    .replace(/(-\d)\d+$/, "$1");
  return { unmasked, masked };
};

export const moneyMask: MaskFunction = (value) => {
  const unmasked = toFixedDecimalString(value);
  const masked = "R$" + formatLocalizedNumber(Number(unmasked));
  return { unmasked, masked };
};

/**
 * Reads a money field as the user types it: every digit is a cent.
 *
 * `moneyMask` cannot do this on its own. It reads a bare "2" as two reais
 * but "R$2,008" as cents, so the first keystroke into an empty field landed
 * in reais and every later one shifted it: typing 280000 stored 200800.00.
 * Use this in `onChange`; keep `moneyMask` for showing a stored value.
 */
export const moneyInputMask: MaskFunction = (value) => {
  const unmasked = (Number(value.replace(/\D+/g, "")) / 100).toFixed(2);
  return { unmasked, masked: moneyMask(unmasked).masked };
};

export const areaMask: MaskFunction = (value) => {
  const unmasked = toFixedDecimalString(value);
  const masked = formatLocalizedNumber(Number(unmasked)) + " m²";
  return { unmasked, masked };
};

export const dateMask: MaskFunction = (value) => {
  const unmasked = value.replace(/\D+/g, "");
  const masked = unmasked
    .replace(/(\d{2})(\d)/, "$1/$2")
    .replace(/(\/\d{2})(\d)/, "$1/$2")
    .replace(/(\/\d{4})\d+$/, "$1");
  return { unmasked, masked };
};

export const dateMonthYearMask: MaskFunction = (value) => {
  const unmasked = value.replace(/\D+/g, "");
  const masked = unmasked
    .replace(/(\d{2})(\d)/, "$1/$2")
    .replace(/(\/\d{2})\d+$/, "$1");
  return { unmasked, masked };
};

export const dateWithDashesMask: MaskFunction = (value) => {
  const unmasked = value.replace(/\D+/g, "");
  const masked = unmasked
    .replace(/(\d{2})(\d)/, "$1-$2")
    .replace(/(-\d{2})(\d)/, "$1-$2")
    .replace(/(-\d{4})\d+$/, "$1");
  return { unmasked, masked };
};

export const hourMask: MaskFunction = (value) => {
  const unmasked = value.replace(/\D+/g, "");
  const masked = unmasked
    .replace(/(\d{2})(\d)/, "$1:$2")
    .replace(/(:\d{2})\d+$/, "$1");
  return { unmasked, masked };
};

export const creditCardNumberMask: MaskFunction = (value) => {
  const unmasked = value.replace(/\D+/g, "");
  const masked = unmasked
    .replace(/(\d{4})(\d)/, "$1 $2")
    .replace(/(\d{4})(\d)/, "$1 $2")
    .replace(/(\d{4})(\d)/, "$1 $2")
    .replace(/(\d{4})(\d)+$/, "$1");
  return { unmasked, masked };
};

export const houseNumberMask: MaskFunction = (value) => {
  const isAlreadyMasked = value.startsWith("N°");
  const unmasked = isAlreadyMasked ? value.slice(2).trim() : value;
  const masked = `N°${unmasked}`;
  return { unmasked, masked };
};

export const timeMask: MaskFunction = (value) => {
  const unmasked = value.replace(/\D+/g, "");
  const masked = unmasked
    .replace(/(\d{2})(\d)/, "$1:$2")
    .replace(/(:\d{2})\d+$/, "$1");
  return { unmasked, masked };
};

/** Characters a complete CPF carries, digits only. */
const CPF_LENGTH = 11;

/** Characters a complete CNPJ carries, letters included. */
const CNPJ_LENGTH = 14;

/** Which of the two Brazilian taxpayer ids a value is on its way to being. */
export type DocumentKind = 'cpf' | 'cnpj';

/**
 * Decides which document is being typed, from the characters alone.
 *
 * The field accepts either one on either profile: an agency may be registered
 * to a sole proprietor's CPF, and a solo broker may hold a CNPJ. So the value
 * settles the question, not the segment the buyer picked upstream.
 *
 * A letter settles it immediately. Since the Receita Federal rollout the raiz
 * and ordem are alphanumeric, so `12.A` is already a CNPJ at three characters
 * — a length-only rule would mask it as a CPF and strip the letter as it was
 * typed. Otherwise the twelfth character is the boundary, because eleven is
 * exactly a CPF and nothing longer can be one.
 *
 * @param value - The raw field value, masked or not.
 * @returns Which document the value is.
 */
export const documentKind = (value: string): DocumentKind => {
  const cleaned = value.replace(/[^0-9A-Za-z]+/g, "");
  if (/[A-Za-z]/.test(cleaned)) return 'cnpj';
  return cleaned.length > CPF_LENGTH ? 'cnpj' : 'cpf';
};

/**
 * Formats a taxpayer id as whichever of the two it is turning out to be.
 *
 * @param value - The raw input.
 * @returns The masked and unmasked value.
 */
export const documentMask: MaskFunction = (value) => (
  documentKind(value) === 'cnpj' ? cnpjMask(value) : cpfMask(value)
);

/**
 * Whether a taxpayer id is long enough to be one.
 *
 * Shape only — the check digits are somebody else's job. What this answers is
 * the question the locked payment box asks: is there still a field to fill in?
 *
 * @param value - The raw field value, masked or not.
 * @returns True when the value is a complete CPF or a complete CNPJ.
 */
export const isCompleteDocument = (value: string): boolean => {
  const { unmasked } = documentMask(value);
  const expected = documentKind(value) === 'cnpj' ? CNPJ_LENGTH : CPF_LENGTH;
  return unmasked.length === expected;
};
